import {
  collectStorageSnapshotForSlug,
  restoreStorageSnapshot
} from "./chunk-I3ZOMX4M.js";
import {
  registerProductsFromTenantStore
} from "./chunk-3YBVQZRI.js";
import {
  isRetiredTenantCatalogSlug
} from "./chunk-N6I6VNBJ.js";
import {
  ensureProductDataBag,
  getTenantProductBySlug,
  loadTenantStore,
  saveTenantStore
} from "./chunk-3TOUJ5O5.js";
import {
  brandToTenantRecord,
  definitionToTenantRecord
} from "./chunk-WAPZEZZJ.js";
import {
  runWithoutBuilderDevSync
} from "./chunk-UEG4HCRI.js";

// src/tenant-products/shipped-catalog.ts
var DEFAULT_SHIPPED_CATALOG_URL = "/tenant-products.json";
var SHIPPED_CATALOG_FILE_VERSION = 1;
var shippedCatalogBootstrap = null;
function resetShippedCatalogBootstrapForTests() {
  shippedCatalogBootstrap = null;
}
function ensureShippedCatalogLoaded(url = DEFAULT_SHIPPED_CATALOG_URL) {
  if (!shippedCatalogBootstrap) {
    shippedCatalogBootstrap = loadShippedTenantCatalog(url).then(() => void 0);
  }
  return shippedCatalogBootstrap;
}
async function loadShippedTenantCatalog(url = DEFAULT_SHIPPED_CATALOG_URL) {
  if (typeof fetch === "undefined") return null;
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (res.status === 404) return null;
    if (!res.ok) {
      console.warn(`[exxat-ds] Shipped catalog fetch failed: ${res.status} ${url}`);
      return null;
    }
    const json = await res.json();
    if (!json?.catalog || !Array.isArray(json.catalog)) {
      console.warn("[exxat-ds] Shipped catalog JSON missing catalog[]");
      return null;
    }
    mergeShippedCatalogIntoTenantStore(json.catalog, json.productData);
    return json;
  } catch (err) {
    console.warn("[exxat-ds] Shipped catalog fetch error", err);
    return null;
  }
}
function mergeRecordObjects(base, overlay) {
  const out = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    const existing = out[key];
    if (value && typeof value === "object" && !Array.isArray(value) && existing && typeof existing === "object" && !Array.isArray(existing)) {
      out[key] = mergeRecordObjects(
        existing,
        value
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}
function mergeShippedProductDataIntoBag(bag, shipped) {
  if (!shipped) return bag;
  return {
    ...bag,
    entities: mergeRecordObjects(
      shipped.entities ?? {},
      bag.entities
    ),
    hubs: mergeRecordObjects(
      shipped.hubs ?? {},
      bag.hubs
    ),
    preferences: mergeRecordObjects(
      shipped.preferences ?? {},
      bag.preferences
    )
  };
}
function mergeShippedCatalogIntoTenantStore(records, productData) {
  runWithoutBuilderDevSync(() => {
    const store = loadTenantStore();
    const userDeletedSlugs = new Set(store.meta.userDeletedSlugs ?? []);
    for (const raw of records) {
      if (!raw?.slug?.trim() || !raw.brandSuffix?.trim()) continue;
      if (isRetiredTenantCatalogSlug(raw.slug)) continue;
      const slugKey = raw.slug.trim().toLowerCase();
      if (userDeletedSlugs.has(slugKey)) continue;
      const existing = getTenantProductBySlug(store, raw.slug);
      const resolvedSource = existing?.source === "user" ? "user" : raw.source ?? "code";
      const record = {
        ...raw,
        source: resolvedSource,
        deletedAt: raw.deletedAt ?? null,
        createdAt: existing?.createdAt ?? raw.createdAt ?? (/* @__PURE__ */ new Date()).toISOString(),
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      };
      const idx = store.catalog.findIndex((p) => p.id === record.id || p.slug === record.slug);
      if (idx >= 0) {
        store.catalog[idx] = record;
      } else {
        store.catalog.push(record);
      }
      const bag = ensureProductDataBag(store, record.id);
      store.products[record.id] = mergeShippedProductDataIntoBag(
        bag,
        productData?.[record.id]
      );
    }
    saveTenantStore(store);
    registerProductsFromTenantStore();
    if (productData) {
      for (const slice of Object.values(productData)) {
        restoreStorageSnapshot(slice.storageSnapshot);
      }
    }
  });
}
function mergeShippedDefinitionsIntoTenantStore(definitions) {
  const records = definitions.map((def) => definitionToTenantRecord(def, "code"));
  mergeShippedCatalogIntoTenantStore(records);
}
function buildShippedTenantCatalogFile(records) {
  const store = loadTenantStore();
  const catalog = records ?? store.catalog.filter((p) => !p.deletedAt);
  const productData = {};
  for (const product of catalog) {
    const bag = store.products[product.id];
    if (!bag) continue;
    const slice = {
      entities: bag.entities,
      hubs: bag.hubs,
      preferences: bag.preferences
    };
    const hasPayload = Object.keys(slice.entities ?? {}).length > 0 || Object.keys(slice.hubs ?? {}).length > 0 || Object.keys(slice.preferences ?? {}).length > 0;
    if (hasPayload) {
      productData[product.id] = slice;
    }
  }
  return {
    v: SHIPPED_CATALOG_FILE_VERSION,
    exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
    // Preserve `source` per record. Builder dev-sync writes user-authored
    // rows (`source: "user"`) so they round-trip through `tenant-products.json`
    // and stay visible in the switcher after refresh / clear-site-data.
    // Hand-authored deploy catalogs that ship with `source: "code"` keep
    // that classification — they continue to merge as code seeds, only
    // surfacing in `customProducts[]` if the catalog explicitly flags them
    // as user-managed.
    catalog: catalog.map((p) => ({ ...p })),
    ...Object.keys(productData).length > 0 ? { productData } : {}
  };
}
function enrichShippedFileWithStorageSnapshots(file) {
  if (typeof window === "undefined") return file;
  const productData = { ...file.productData ?? {} };
  for (const product of file.catalog) {
    if (product.deletedAt) continue;
    const snapshot = collectStorageSnapshotForSlug(product.slug);
    if (Object.keys(snapshot).length === 0) continue;
    const existing = productData[product.id] ?? {};
    productData[product.id] = { ...existing, storageSnapshot: snapshot };
  }
  return {
    ...file,
    ...Object.keys(productData).length > 0 ? { productData } : {}
  };
}
function buildBuilderShippedCatalogFile() {
  return enrichShippedFileWithStorageSnapshots(buildShippedTenantCatalogFile());
}
function isDevBrowser() {
  try {
    return typeof window !== "undefined" && import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}
function serializeShippedTenantCatalogFile(file) {
  return `${JSON.stringify(file, null, 2)}
`;
}
function downloadShippedTenantCatalog(records, filename = "tenant-products.json") {
  if (typeof document === "undefined") return;
  const body = serializeShippedTenantCatalogFile(
    isDevBrowser() ? buildBuilderShippedCatalogFile() : buildShippedTenantCatalogFile(records)
  );
  const blob = new Blob([body], { type: "application/json" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}
function tenantRecordFromCustomBrand(suffix, brandColor) {
  return brandToTenantRecord({ suffix, brandColor });
}

export {
  DEFAULT_SHIPPED_CATALOG_URL,
  SHIPPED_CATALOG_FILE_VERSION,
  resetShippedCatalogBootstrapForTests,
  ensureShippedCatalogLoaded,
  loadShippedTenantCatalog,
  mergeShippedProductDataIntoBag,
  mergeShippedCatalogIntoTenantStore,
  mergeShippedDefinitionsIntoTenantStore,
  buildShippedTenantCatalogFile,
  enrichShippedFileWithStorageSnapshots,
  buildBuilderShippedCatalogFile,
  serializeShippedTenantCatalogFile,
  downloadShippedTenantCatalog,
  tenantRecordFromCustomBrand
};
